import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import useSignalRListener from '@app/src/hooks/signalRListener'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { processProviderRisk } from '@app/src/pages/TopicalRisk/CompanyPage/providerRiskSnapshot.logic'
import { SortOrder } from '@app/src/types/filter'
import type { SustainabilityTopicProviderRisk } from '@app/src/types/topicalRisk'
import { ONE_MINUTE_MS } from '@app/src/wf-constants'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

/**
 * The query key is derived from this payload, so the page and the topic drawer must build it
 * identically to share one cache entry and stay in sync.
 */
export function providerRiskPayload(providerId: number) {
  return {
    filter: [{ name: 'providerId', filters: [{ operator: Operators.EqualTo, value: providerId }] }],
    include: [
      'riskStatus',
      'provider.spends',
      'provider.providerOwners.user',
      'provider.organization.country',
      'provider.organization.subIndustryNaceCode',
      'sustainabilityAreaRisks.sustainabilityTopicRisks.countryRisks.country',
      'sustainabilityAreaRisks.sustainabilityTopicRisks.countryRisks.riskType',
      'sustainabilityAreaRisks.sustainabilityTopicRisks.industryRisks.subNaceCode',
      'sustainabilityAreaRisks.sustainabilityTopicRisks.industryRisks.riskType',
      'sustainabilityAreaRisks.sustainabilityTopicRisks.sustainabilityTopic',
      'sustainabilityAreaRisks.sustainabilityTopicRisks.assessmentTemplate',
      'sustainabilityAreaRisks.sustainabilityTopicRisks.assessmentTemplateSection',
      'sustainabilityAreaRisks.sustainabilityTopicRisks.assessmentTemplateSection.assessmentTemplate',
      'answerFlagRuleResults.answerFlagRule',
    ],
    sort: { target: 'createdAt', order: SortOrder.DESCENDING },
  }
}

export function useProviderRiskSnapshot(providerId: number) {
  const { items: providerRiskHistory = [], isLoading } = useFetchCollectionWithPost<SustainabilityTopicProviderRisk>({
    key: FetchKey.ProviderRisk,
    endpoint: endpoints.topicalRisk,
    payload: providerRiskPayload(providerId),
    options: { staleTime: ONE_MINUTE_MS },
  })

  const currentProviderRisk = useMemo(() => processProviderRisk(providerRiskHistory[0] ?? null), [providerRiskHistory])

  return { providerRiskHistory, currentProviderRisk, isLoading }
}

/**
 * Recalculation is asynchronous, so a fresh snapshot can land minutes after the edit that
 * triggered it. Without this the supplier risk page keeps showing the pre-change country and
 * "last updated" date until the user reloads. Belongs on the page, not on every consumer of
 * the snapshot - one listener refetches the shared query for all of them.
 */
export function useRefetchProviderRiskOnRecalculation() {
  const queryClient = useQueryClient()

  const refetchProviderRisk = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: [FetchKey.ProviderRisk] })
  }, [queryClient])

  useSignalRListener('TopicalRiskCalculated', refetchProviderRisk)
}
